import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from '../styles/step1WritingModal.module.css';
import {
  Step1PresentationPlan,
  Step1WritingInventoryPayload,
  Step1WritingMetadata,
  Step1WritingMode,
  isStep1PathSelected,
} from '../types/step1Writing';
import {
  buildMetadataFromState,
  clearStep1WritingAll,
  downloadOriginalFile,
  listStep1ArchiveSummaries,
  loadStep1WritingPayload,
  payloadHasMeaningfulStep1Content,
  readStep1LocalBackup,
  replaceStep1WritingPayload,
  restoreStep1FromArchive,
  saveStep1WritingPayload,
  startFreshStep1WritingPayload,
  Step1ArchiveSummary,
  Step1SaveKind,
} from '../services/step1WritingService';
import {
  formatStoryLengthLabel,
  estimateReadTime,
  formatAloudDurationLabel,
  formatWordCountLabel,
} from '../utils/readTimeEstimate';
import { STEP1_CHAPTERS, chapterTitle } from '../config/step1CoverageTopics';
import {
  STEP1_LIFE_STORY_PROMPTS,
  globalPromptIndex,
  promptNudge,
  promptPlaceholder,
  promptText,
  promptsForChapter,
} from '../config/step1LifeStoryPrompts';
import { blocksForFlowWeave, fingerprintWizardAnswers, localFlowWeave, localDraftChecklist } from '../utils/step1DraftGenerator';
import { extractStep1UploadFile } from '../utils/step1FileExtract';
import StepShareDialog from './StepShareDialog';
import Step1RecoverOverlay from './Step1RecoverOverlay';
import { generateFirstStepShareHtml } from '../services/firstStepShareHtml';
import { downloadFirstStepStory, FirstStepDownloadFormat } from '../utils/firstStepDownload';
import { createDefaultStep1WritingMetadata } from '../utils/step1WritingMigration';
import {
  getStep1EncryptionReminder,
  getStep1MethodSteps,
  getStep1PathOptions,
  getStep1WritingTemplate,
  methodStepIndex,
  methodTitle,
} from '../config/step1WritingMethodGuide';
import { useDialog } from '../context/DialogContext';
import { useLocale } from '../context/LocaleContext';
import { AuthenticationError } from '../utils/authError';

interface Step1WritingModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: { name?: string; display_name?: string; email?: string };
  metadata: Step1WritingMetadata;
  onSaveMetadata: (metadata: Step1WritingMetadata) => Promise<void>;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Above this word count, story textareas use a compact scrollable panel by default */
const LONG_DRAFT_WORD_THRESHOLD = 400;

const Step1WritingModal: React.FC<Step1WritingModalProps> = ({
  isOpen,
  onClose,
  user,
  metadata,
  onSaveMetadata,
}) => {
  const { showConfirm, showAlert } = useDialog();
  const { t, tStep, locale } = useLocale();
  const [payload, setPayload] = useState<Step1WritingInventoryPayload | null>(null);
  const [localMeta, setLocalMeta] = useState<Step1WritingMetadata>(metadata);
  const [showPathPicker, setShowPathPicker] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [wizardPromptIndex, setWizardPromptIndex] = useState(0);
  const [isAssembling, setIsAssembling] = useState(false);
  const [isPayloadLoading, setIsPayloadLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [decryptFailed, setDecryptFailed] = useState(false);
  const [isStartingFresh, setIsStartingFresh] = useState(false);
  const draftAnswersBaselineRef = useRef<string | null>(null);
  const payloadLoadSucceededRef = useRef(false);
  const [isStoryShareOpen, setIsStoryShareOpen] = useState(false);
  const [isDraftEditorOpen, setIsDraftEditorOpen] = useState(false);
  const [draftEditBuffer, setDraftEditBuffer] = useState('');
  const [draftEditBaseline, setDraftEditBaseline] = useState('');
  const [draftEditorConfig, setDraftEditorConfig] = useState<{
    title: string;
    ariaLabel: string;
    placeholder?: string;
  } | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const draftEditorRef = useRef<HTMLDivElement | null>(null);
  const presentationSectionRef = useRef<HTMLDivElement | null>(null);
  const reviewSectionRef = useRef<HTMLDivElement | null>(null);
  const uploadFileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadPickerFileName, setUploadPickerFileName] = useState('');
  const [isStoryViewOpen, setIsStoryViewOpen] = useState(false);
  const [isRecoverOpen, setIsRecoverOpen] = useState(false);
  const [archiveSummaries, setArchiveSummaries] = useState<Step1ArchiveSummary[]>([]);
  const [isArchivesLoading, setIsArchivesLoading] = useState(false);
  const [showMissingStoryOffer, setShowMissingStoryOffer] = useState(false);
  const [isDownloadPickerOpen, setIsDownloadPickerOpen] = useState(false);
  const [isClearAllOpen, setIsClearAllOpen] = useState(false);
  const [clearAllAcknowledged, setClearAllAcknowledged] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const autoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const payloadRef = useRef<Step1WritingInventoryPayload | null>(null);
  const metaRef = useRef(localMeta);

  useEffect(() => {
    payloadRef.current = payload;
  }, [payload]);
  useEffect(() => {
    metaRef.current = localMeta;
  }, [localMeta]);

  const userBackupKey = user?.email || user?.name || 'anonymous';

  const openRecoverPanel = useCallback(async () => {
    setIsRecoverOpen(true);
    setIsArchivesLoading(true);
    try {
      const summaries = await listStep1ArchiveSummaries();
      setArchiveSummaries(summaries);
    } catch {
      setArchiveSummaries([]);
    } finally {
      setIsArchivesLoading(false);
    }
  }, []);

  const applyPayloadFromServer = useCallback((p: Step1WritingInventoryPayload) => {
    setPayload(p);
    payloadLoadSucceededRef.current = true;
    draftAnswersBaselineRef.current =
      p.assembledAnswersFingerprint ?? fingerprintWizardAnswers(p.wizardAnswers || {});
    const idx = p.wizardStepIndex ?? 0;
    setWizardPromptIndex(Math.min(idx, STEP1_LIFE_STORY_PROMPTS.length - 1));
  }, []);

  const restoreArchive = useCallback(
    (summary: Step1ArchiveSummary) => {
      showConfirm({
        title: t('step.1.writing.restore_confirm_title'),
        message: t('step.1.writing.restore_confirm_archive', { label: summary.label }),
        confirmText: t('step.1.writing.restore_confirm_ok'),
        cancelText: t('step.1.writing.cancel'),
        variant: 'warning',
        onConfirm: async () => {
          try {
            const restored = await restoreStep1FromArchive(summary.version, userBackupKey);
            applyPayloadFromServer(restored);
            const built = buildMetadataFromState(localMeta, restored);
            await onSaveMetadata({ ...built, lastWorkedAt: new Date().toISOString() });
            setLocalMeta(built);
            setIsRecoverOpen(false);
            setShowMissingStoryOffer(false);
            showAlert({ message: t('step.1.writing.restore_success'), variant: 'success' });
          } catch {
            showAlert({ message: t('step.1.writing.restore_failed'), variant: 'warning' });
          }
        },
      });
    },
    [applyPayloadFromServer, localMeta, onSaveMetadata, showAlert, showConfirm, t, userBackupKey]
  );

  const restoreLocalBackup = useCallback(() => {
    const local = readStep1LocalBackup(userBackupKey);
    if (!local) {
      showAlert({ message: t('step.1.writing.no_local_save'), variant: 'info' });
      return;
    }
    const savedLabel = new Date(local.savedAt).toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    showConfirm({
      title: t('step.1.writing.restore_confirm_title'),
      message: t('step.1.writing.restore_confirm_local', { savedAt: savedLabel }),
      confirmText: t('step.1.writing.restore_confirm_ok'),
      cancelText: t('step.1.writing.cancel'),
      variant: 'warning',
      onConfirm: async () => {
        try {
          await replaceStep1WritingPayload(local.payload, {
            kind: 'restore',
            archiveReason: 'Before restoring device backup',
            localBackupUserKey: userBackupKey,
          });
          applyPayloadFromServer(local.payload);
          const built = buildMetadataFromState(localMeta, local.payload);
          await onSaveMetadata({ ...built, lastWorkedAt: new Date().toISOString() });
          setLocalMeta(built);
          setIsRecoverOpen(false);
          setShowMissingStoryOffer(false);
          showAlert({ message: t('step.1.writing.restore_success'), variant: 'success' });
        } catch {
          showAlert({ message: t('step.1.writing.restore_failed'), variant: 'warning' });
        }
      },
    });
  }, [
    applyPayloadFromServer,
    locale,
    localMeta,
    onSaveMetadata,
    showAlert,
    showConfirm,
    t,
    userBackupKey,
  ]);

  const checkForMissingStoryOffer = useCallback(
    async (serverPayload: Step1WritingInventoryPayload, writingMeta: Step1WritingMetadata) => {
      if (payloadHasMeaningfulStep1Content(serverPayload) || !writingMeta.hasContent) {
        setShowMissingStoryOffer(false);
        return;
      }
      const local = readStep1LocalBackup(userBackupKey);
      const hasLocal = !!(local && payloadHasMeaningfulStep1Content(local.payload));
      let hasArchives = false;
      try {
        const summaries = await listStep1ArchiveSummaries();
        hasArchives = summaries.length > 0;
      } catch {
        hasArchives = false;
      }
      setShowMissingStoryOffer(hasLocal || hasArchives);
    },
    [userBackupKey]
  );

  const persistAll = useCallback(
    async (
      nextPayload: Step1WritingInventoryPayload,
      nextMeta: Step1WritingMetadata,
      saveKind: Step1SaveKind = 'autosave',
      archiveReason?: string
    ) => {
      setSaveStatus('saving');
      try {
        const built = buildMetadataFromState(nextMeta, nextPayload);
        await saveStep1WritingPayload(nextPayload, {
          kind: saveKind,
          archiveReason,
          localBackupUserKey: userBackupKey,
        });
        await onSaveMetadata({ ...built, lastWorkedAt: new Date().toISOString() });
        setLocalMeta(built);
        setSaveStatus('saved');
      } catch (err) {
        setSaveStatus('error');
        if (err instanceof Error && err.message === 'STEP1_WRITING_WIPE_BLOCKED') {
          showConfirm({
            title: t('step.1.writing.protected_title'),
            message: t('step.1.writing.protected_message'),
            confirmText: t('step.1.writing.view_earlier_saves'),
            cancelText: t('step.1.writing.protected_ok'),
            variant: 'warning',
            onConfirm: () => void openRecoverPanel(),
          });
          try {
            const existing = await loadStep1WritingPayload();
            setPayload(existing);
            payloadLoadSucceededRef.current = true;
          } catch {
            payloadLoadSucceededRef.current = false;
            setLoadError(t('step.1.writing.error.reload_failed'));
          }
        } else if (err instanceof Error && err.message === 'STEP1_WRITING_LOAD_FAILED') {
          showAlert({
            title: t('step.1.writing.save_failed_title'),
            message: t('step.1.writing.save_verify_failed'),
            variant: 'warning',
          });
        }
      }
    },
    [onSaveMetadata, showAlert, showConfirm, openRecoverPanel, t, userBackupKey]
  );

  const reloadPayload = useCallback(async () => {
    setIsPayloadLoading(true);
    setLoadError('');
    setDecryptFailed(false);
    setShowMissingStoryOffer(false);
    try {
      const p = await loadStep1WritingPayload();
      applyPayloadFromServer(p);
      await checkForMissingStoryOffer(p, metadata);
    } catch (err) {
      payloadLoadSucceededRef.current = false;
      const local = readStep1LocalBackup(userBackupKey);
      if (err instanceof Error && err.message === 'STEP1_WRITING_DECRYPT_FAILED') {
        setDecryptFailed(true);
        setLoadError(t('step.1.writing.error.decrypt'));
      } else if (err instanceof AuthenticationError) {
        setLoadError(t('step.1.writing.error.auth', { message: err.message }));
      } else if (local && payloadHasMeaningfulStep1Content(local.payload)) {
        setLoadError(t('step.1.writing.error.load_with_local'));
      } else {
        setLoadError(t('step.1.writing.error.load_generic'));
      }
      setPayload(null);
    } finally {
      setIsPayloadLoading(false);
    }
  }, [applyPayloadFromServer, checkForMissingStoryOffer, metadata, t, userBackupKey]);

  const startFreshDraft = useCallback(async () => {
    setIsStartingFresh(true);
    try {
      const fresh = await startFreshStep1WritingPayload(userBackupKey);
      applyPayloadFromServer(fresh);
      const built = buildMetadataFromState(localMeta, fresh);
      await onSaveMetadata({ ...built, lastWorkedAt: new Date().toISOString() });
      setLocalMeta(built);
      setDecryptFailed(false);
      setLoadError('');
    } catch {
      showAlert({
        message: t('step.1.writing.error.start_fresh_failed'),
        variant: 'warning',
      });
    } finally {
      setIsStartingFresh(false);
    }
  }, [applyPayloadFromServer, localMeta, onSaveMetadata, showAlert, t, userBackupKey]);

  const scheduleSave = useCallback(
    (nextPayload: Step1WritingInventoryPayload, nextMeta: Step1WritingMetadata) => {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
      autoSaveRef.current = setTimeout(() => {
        persistAll(nextPayload, nextMeta);
      }, 800);
    },
    [persistAll]
  );

  useEffect(() => {
    if (!isOpen) {
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
        autoSaveRef.current = null;
      }
      if (payloadLoadSucceededRef.current && payloadRef.current) {
        void persistAll(payloadRef.current, metaRef.current, 'flush', 'Automatic snapshot on close');
      }
      payloadLoadSucceededRef.current = false;
      setLoadError('');
      setShowMissingStoryOffer(false);
      draftAnswersBaselineRef.current = null;
      return;
    }
    setLocalMeta(metadata);
    setShowPathPicker(!isStep1PathSelected(metadata));
    setSaveStatus('idle');
    setIsDraftEditorOpen(false);
    setIsStoryViewOpen(false);
    setIsDownloadPickerOpen(false);
    setIsClearAllOpen(false);
    setClearAllAcknowledged(false);
    setPayload(null);
    payloadLoadSucceededRef.current = false;
    void reloadPayload();
    // Load once when the modal opens — not on metadata autosave while it stays open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    };
  }, []);

  const draftChecklistIssues = useMemo(() => {
    const text = (payload?.contentText || '').trim();
    if (!text) return [];
    return localDraftChecklist(text, payload?.wizardAnswers);
  }, [payload]);

  if (!isOpen) return null;

  if (isPayloadLoading || !payload) {
    return (
      <>
        <div className={`${styles.overlay} ${styles.overlayBusy}`} onClick={onClose}>
          <div
            className={`${styles.modal} ${styles.modalBusy}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="s1-writing-title"
            aria-busy={isPayloadLoading}
          >
            <div className={styles.header}>
              <div className={styles.headerInner}>
                <h2 id="s1-writing-title">{t('ui.FIRST_STEP_WRITING_WORKSPACE')}</h2>
              </div>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={onClose}
                aria-label={t('step.1.writing.close')}
              >
                ×
              </button>
            </div>
            <div className={`${styles.body} ${styles.bodyBusy}`}>
              {loadError ? (
                <div className={styles.loadErrorBlock}>
                  <p className={styles.loadErrorMessage}>{loadError}</p>
                  <div className={styles.loadErrorActions}>
                    <button type="button" className={styles.btnPrimary} onClick={() => void reloadPayload()}>
                      {t('step.1.writing.try_again')}
                    </button>
                    {decryptFailed && (
                      <button
                        type="button"
                        className={styles.btnPrimary}
                        onClick={() => void startFreshDraft()}
                        disabled={isStartingFresh}
                      >
                        {isStartingFresh
                          ? t('step.1.writing.starting')
                          : t('step.1.writing.start_new_draft')}
                      </button>
                    )}
                    <button type="button" className={styles.btn} onClick={() => void openRecoverPanel()}>
                      {t('step.1.writing.view_earlier_saves')}
                    </button>
                  </div>
                </div>
              ) : (
                <p className={styles.loadingMessage}>{t('step.1.writing.loading')}</p>
              )}
            </div>
          </div>
        </div>
        {isRecoverOpen && (
          <Step1RecoverOverlay
            userBackupKey={userBackupKey}
            archiveSummaries={archiveSummaries}
            isArchivesLoading={isArchivesLoading}
            onClose={() => setIsRecoverOpen(false)}
            onRestoreArchive={restoreArchive}
            onRestoreLocal={restoreLocalBackup}
          />
        )}
      </>
    );
  }

  const currentPayload = payload;

  const contentText = currentPayload.contentText || '';
  const stats = estimateReadTime(contentText);
  const lengthLabel = contentText.trim() ? formatStoryLengthLabel(stats) : '';
  const wizardAnswersFingerprint = fingerprintWizardAnswers(currentPayload.wizardAnswers || {});
  const hasAssembledDraft = contentText.trim().length > 0;
  const draftAnswersBaseline =
    currentPayload.assembledAnswersFingerprint ?? draftAnswersBaselineRef.current;
  const draftNeedsReassemble =
    hasAssembledDraft &&
    draftAnswersBaseline != null &&
    wizardAnswersFingerprint !== draftAnswersBaseline;
  const draftIsCurrent = hasAssembledDraft && !draftNeedsReassemble;

  const stepIndexFor = (id: string): number => {
    if (!localMeta.mode) return 0;
    return methodStepIndex(localMeta.mode, id);
  };

  const renderMethodStepHeader = (stepIndex: number) => {
    if (!localMeta.mode) return null;
    const step = getStep1MethodSteps(localMeta.mode)[stepIndex];
    if (!step) return null;
    return (
      <h3 className={styles.methodStepHeader}>
        <span className={styles.methodStepNumber}>{stepIndex + 1}.</span>{' '}
        <strong>{step.title}</strong>
        {' — '}
        <span className={styles.methodStepHeaderDetail}>{step.body}</span>
      </h3>
    );
  };

  const showsDraftChecklist = localMeta.mode === 'typed' || localMeta.mode === 'upload';

  const isLongDraft = stats.wordCount > LONG_DRAFT_WORD_THRESHOLD;

  const openDraftEditor = (options: { ariaLabel: string; placeholder?: string }) => {
    const baseline = contentText;
    setDraftEditBaseline(baseline);
    setDraftEditBuffer(baseline);
    setDraftEditorConfig({
      ariaLabel: options.ariaLabel,
      placeholder: options.placeholder,
      title: t('step.1.writing.draft_editor_title'),
    });
    setIsDraftEditorOpen(true);
  };

  const closeDraftEditor = () => {
    setIsDraftEditorOpen(false);
    setDraftEditorConfig(null);
  };

  const requestCloseDraftEditor = () => {
    if (draftEditBuffer !== draftEditBaseline) {
      showConfirm({
        title: t('step.1.writing.discard_title'),
        message: t('step.1.writing.discard_message'),
        confirmText: t('step.1.writing.discard_ok'),
        cancelText: t('step.1.writing.keep_editing'),
        variant: 'warning',
        onConfirm: closeDraftEditor,
      });
      return;
    }
    closeDraftEditor();
  };

  const saveDraftEditor = () => {
    const applyDraftSave = async () => {
      const nextPayload = { ...currentPayload, contentText: draftEditBuffer };
      await persistAll(nextPayload, localMeta, 'manual', 'Edited draft in draft editor');
      setPayload(nextPayload);
      closeDraftEditor();
    };
    const baselineLen = draftEditBaseline.trim().length;
    const bufferLen = draftEditBuffer.trim().length;
    if (baselineLen >= 50 && bufferLen === 0) {
      showConfirm({
        title: t('step.1.writing.save_empty_title'),
        message: t('step.1.writing.save_empty_message'),
        confirmText: t('step.1.writing.save_empty_ok'),
        cancelText: t('step.1.writing.keep_editing'),
        variant: 'warning',
        onConfirm: () => void applyDraftSave(),
      });
      return;
    }
    void applyDraftSave();
  };

  const renderStoryTextarea = (options: {
    ariaLabel: string;
    placeholder?: string;
    editorRef?: React.RefObject<HTMLDivElement | null>;
  }) => {
    if (isLongDraft) {
      const aloud = formatAloudDurationLabel(stats.wordCount);
      return (
        <div className={styles.draftSummary} ref={options.editorRef}>
          <p>
            {t('step.1.writing.draft_ready_lead')}{' '}
            <strong>{formatWordCountLabel(stats.wordCount)}</strong>
            {aloud ? <> · {aloud}</> : null}
            {t('step.1.writing.draft_ready_tail')}
          </p>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => openDraftEditor(options)}
          >
            {t('step.1.writing.open_draft_editor')}
          </button>
        </div>
      );
    }

    return (
      <div className={styles.draftEditorBlock} ref={options.editorRef}>
        <textarea
          className={styles.textarea}
          value={contentText}
          onChange={(e) => updatePayload({ contentText: e.target.value })}
          placeholder={options.placeholder}
          aria-label={options.ariaLabel}
        />
      </div>
    );
  };

  const updatePayload = (patch: Partial<Step1WritingInventoryPayload>, autoSave = true) => {
    const next = { ...currentPayload, ...patch };
    setPayload(next);
    if (autoSave && payloadLoadSucceededRef.current) {
      scheduleSave(next, { ...localMeta, mode: localMeta.mode });
    }
  };

  const switchMode = async (mode: Step1WritingMode) => {
    const apply = async () => {
      const nextMeta: Step1WritingMetadata = { ...localMeta, mode, pathSelected: true };
      setLocalMeta(nextMeta);
      setShowPathPicker(false);
      await persistAll(currentPayload, nextMeta, 'manual', 'Changed writing method');
    };
    if (payloadHasMeaningfulStep1Content(currentPayload)) {
      showConfirm({
        title: t('step.1.writing.change_method_title'),
        message: t('step.1.writing.change_method_message'),
        confirmText: t('step.1.writing.change_method'),
        cancelText: t('step.1.writing.cancel'),
        onConfirm: () => void apply(),
      });
      return;
    }
    await apply();
  };

  const returnToPathChoices = async () => {
    const apply = async () => {
      const nextMeta: Step1WritingMetadata = {
        ...localMeta,
        pathSelected: false,
        mode: undefined,
      };
      setLocalMeta(nextMeta);
      setShowPathPicker(true);
      await persistAll(currentPayload, nextMeta, 'manual', 'Returned to method chooser');
    };
    if (payloadHasMeaningfulStep1Content(currentPayload)) {
      showConfirm({
        title: t('step.1.writing.return_chooser_title'),
        message: t('step.1.writing.return_chooser_message'),
        confirmText: t('step.1.writing.continue'),
        cancelText: t('step.1.writing.cancel'),
        onConfirm: () => void apply(),
      });
      return;
    }
    await apply();
  };

  const updatePresentationPlan = (patch: Partial<Step1PresentationPlan>) => {
    const plan = { ...(currentPayload.presentationPlan || {}), ...patch, updatedAt: new Date().toISOString() };
    updatePayload({ presentationPlan: plan });
  };

  const currentPrompt = STEP1_LIFE_STORY_PROMPTS[wizardPromptIndex];
  const isLastWizardPrompt = wizardPromptIndex >= STEP1_LIFE_STORY_PROMPTS.length - 1;
  const currentAnswer = currentPrompt ? currentPayload.wizardAnswers[currentPrompt.id] : undefined;

  const setWizardExample = (text: string) => {
    if (!currentPrompt) return;
    const prev = currentPayload.wizardAnswers[currentPrompt.id] || { examples: [''] };
    updatePayload({
      wizardAnswers: {
        ...currentPayload.wizardAnswers,
        [currentPrompt.id]: { ...prev, examples: [text], status: 'answered' },
      },
      wizardStepIndex: wizardPromptIndex,
      wizardChapterId: currentPrompt.chapterId,
    });
  };

  const skipWizardPrompt = () => {
    if (!currentPrompt) return;
    updatePayload({
      wizardAnswers: {
        ...currentPayload.wizardAnswers,
        [currentPrompt.id]: { examples: [], status: 'skipped' },
      },
      wizardStepIndex: wizardPromptIndex,
    });
    goWizardNext();
  };

  const goWizardNext = () => {
    if (wizardPromptIndex < STEP1_LIFE_STORY_PROMPTS.length - 1) {
      setWizardPromptIndex(wizardPromptIndex + 1);
    }
  };

  const goWizardPrev = () => {
    if (wizardPromptIndex > 0) setWizardPromptIndex(wizardPromptIndex - 1);
  };

  const handleAssembleDraft = async () => {
    const blocks = blocksForFlowWeave(currentPayload.wizardAnswers);
    if (blocks.length === 0) {
      showAlert({ message: t('step.1.writing.no_answers_yet'), variant: 'info' });
      return;
    }
    const run = async () => {
      await runAssembleDraft(blocks);
    };
    if (hasAssembledDraft && draftNeedsReassemble) {
      showConfirm({
        title: t('step.1.writing.update_draft_title'),
        message: t('step.1.writing.update_draft_message'),
        confirmText: t('step.1.writing.update_draft_ok'),
        cancelText: t('step.1.writing.cancel'),
        onConfirm: () => void run(),
      });
      return;
    }
    if (contentText.trim().length > 50) {
      showConfirm({
        title: t('step.1.writing.assemble_title'),
        message: t('step.1.writing.assemble_message'),
        confirmText: t('step.1.writing.assemble_ok'),
        cancelText: t('step.1.writing.cancel'),
        onConfirm: () => void run(),
      });
      return;
    }
    await run();
  };

  const runAssembleDraft = async (blocks: string[]) => {
    setIsAssembling(true);
    try {
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      const draft = localFlowWeave(blocks);
      const answersFingerprint = fingerprintWizardAnswers(currentPayload.wizardAnswers);
      const wasFirstAssembly = !hasAssembledDraft;
      const nextPayload: Step1WritingInventoryPayload = {
        ...currentPayload,
        contentText: draft,
        assembledAnswersFingerprint: answersFingerprint,
      };
      await persistAll(
        nextPayload,
        localMeta,
        'destructive',
        'Assembled draft from walkthrough answers'
      );
      setPayload(nextPayload);
      draftAnswersBaselineRef.current = answersFingerprint;
      if (wasFirstAssembly) {
        const assembledWordCount = draft.trim().split(/\s+/).filter(Boolean).length;
        const message =
          assembledWordCount > LONG_DRAFT_WORD_THRESHOLD
            ? t('step.1.writing.assembled_long')
            : t('step.1.writing.assembled_short');
        showAlert({ message, variant: 'info' });
      }
    } finally {
      setIsAssembling(false);
    }
  };

  const isBodyBusy = isAssembling;

  const renderMethodBanner = () => {
    if (!localMeta.mode) return null;
    return (
      <div className={styles.methodBanner}>
        <div className={styles.methodBannerRow}>
          <div className={styles.methodBannerLeft}>
            <span className={styles.methodBannerLabel}>
              {t('step.1.writing.method_label')} <strong>{methodTitle(localMeta.mode)}</strong>
            </span>
            <button type="button" className={styles.btnLink} onClick={returnToPathChoices}>
              {t('step.1.writing.change_method')}
            </button>
          </div>
          {lengthLabel && <span className={styles.methodLength}>{lengthLabel}</span>}
        </div>
      </div>
    );
  };

  const handleUpload = async (file: File) => {
    setUploadError('');
    try {
      const extracted = await extractStep1UploadFile(file);
      const run = async () => {
        const next = {
          ...currentPayload,
          contentText: extracted.text,
          originalFileName: extracted.fileName,
          originalMimeType: extracted.mimeType,
          originalFileBase64: extracted.base64,
        };
        setPayload(next);
        const nextMeta: Step1WritingMetadata = { ...localMeta, mode: 'upload', pathSelected: true };
        setLocalMeta(nextMeta);
        await persistAll(next, nextMeta, 'destructive', `Uploaded file: ${extracted.fileName}`);
      };
      if (payloadHasMeaningfulStep1Content(currentPayload)) {
        showConfirm({
          title: t('step.1.writing.upload_replace_title'),
          message: t('step.1.writing.upload_replace_message'),
          confirmText: t('step.1.writing.upload_replace_ok'),
          cancelText: t('step.1.writing.cancel'),
          onConfirm: () => void run(),
        });
        return;
      }
      await run();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : t('step.1.writing.upload_failed'));
    }
  };

  const firstStepLastEditedAt =
    currentPayload.lastSavedAt || localMeta.lastWorkedAt || localMeta.lastUpdated;

  const handlePrint = () => {
    const html = generateFirstStepShareHtml(contentText, {
      lastEditedAt: firstStepLastEditedAt,
      includeShareNotice: false,
    });
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.print();
  };

  const handleDownload = async (format: FirstStepDownloadFormat) => {
    try {
      await downloadFirstStepStory(format, contentText, firstStepLastEditedAt);
      setIsDownloadPickerOpen(false);
    } catch {
      showAlert({ message: t('step.1.writing.download_failed'), variant: 'warning' });
    }
  };

  const openClearAllDialog = () => {
    setClearAllAcknowledged(false);
    setIsClearAllOpen(true);
  };

  const handleClearAll = async () => {
    if (!clearAllAcknowledged || isClearingAll) return;
    setIsClearingAll(true);
    try {
      const empty = await clearStep1WritingAll(userBackupKey);
      const clearedMeta = createDefaultStep1WritingMetadata(
        localMeta.migrationSource || 'new_user_choose_path'
      );
      applyPayloadFromServer(empty);
      await onSaveMetadata(clearedMeta);
      setLocalMeta(clearedMeta);
      setShowPathPicker(true);
      setWizardPromptIndex(0);
      setIsClearAllOpen(false);
      setClearAllAcknowledged(false);
      setIsStoryViewOpen(false);
      setShowMissingStoryOffer(false);
      setSaveStatus('saved');
      showAlert({ message: t('step.1.writing.clear_success'), variant: 'success' });
    } catch {
      showAlert({ message: t('step.1.writing.clear_failed'), variant: 'warning' });
    } finally {
      setIsClearingAll(false);
    }
  };

  const hasClearableContent =
    payloadHasMeaningfulStep1Content(currentPayload) ||
    !!localMeta.externalNote?.trim() ||
    !!(localMeta.presentationPlan?.targetDate || localMeta.presentationPlan?.venue) ||
    !!currentPayload.originalFileName;

  const openStoryShare = () => {
    if (!contentText.trim()) {
      showAlert({ message: t('step.1.writing.share_empty'), variant: 'info' });
      return;
    }
    showConfirm({
      title: t('step.1.writing.share_story'),
      message: t('step.1.writing.share_confirm_message'),
      confirmText: t('step.1.writing.continue'),
      cancelText: t('step.1.writing.cancel'),
      onConfirm: () => setIsStoryShareOpen(true),
    });
  };

  const renderPresentationPlan = (stepIndex: number) => (
    <div ref={presentationSectionRef}>
      {renderMethodStepHeader(stepIndex)}
      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="s1-target-date">
          {t('step.1.presentation.target_date_label')}
        </label>
        <input
          id="s1-target-date"
          type="date"
          className={styles.input}
          value={currentPayload.presentationPlan?.targetDate || ''}
          onChange={(e) => updatePresentationPlan({ targetDate: e.target.value })}
        />
      </div>
      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="s1-venue">
          {t('step.1.presentation.venue_label')}
        </label>
        <select
          id="s1-venue"
          className={styles.select}
          value={currentPayload.presentationPlan?.venue || ''}
          onChange={(e) =>
            updatePresentationPlan({
              venue: (e.target.value || undefined) as Step1PresentationPlan['venue'],
            })
          }
        >
          <option value="">{t('step.1.presentation.venue_select')}</option>
          <option value="workshop">{t('step.1.venue.workshop')}</option>
          <option value="home_meeting">{t('step.1.venue.home_meeting')}</option>
          <option value="sponsor_only">{t('step.1.venue.sponsor_only')}</option>
          <option value="other">{t('step.1.venue.other')}</option>
        </select>
      </div>
      {currentPayload.presentationPlan?.venue === 'other' && (
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="s1-venue-note">
            {t('step.1.presentation.venue_note_label')}
          </label>
          <input
            id="s1-venue-note"
            className={styles.input}
            value={currentPayload.presentationPlan?.venueNote || ''}
            onChange={(e) => updatePresentationPlan({ venueNote: e.target.value })}
          />
        </div>
      )}
    </div>
  );

  const renderPathPicker = () => (
    <>
      <h3 className={styles.sectionTitle}>{t('step.1.writing.path_picker_title')}</h3>
      <p style={{ fontSize: '0.95rem', color: '#555', marginTop: 0 }}>
        {t('step.1.writing.path_picker_intro')}
      </p>
      <div className={styles.pathGrid}>
        {getStep1PathOptions().map((opt) => (
          <button
            key={opt.mode}
            type="button"
            className={`${styles.pathCard} ${
              isStep1PathSelected(localMeta) && localMeta.mode === opt.mode ? styles.pathCardSelected : ''
            }`}
            onClick={() => switchMode(opt.mode)}
          >
            <h3>{opt.title}</h3>
            <p>{opt.description}</p>
          </button>
        ))}
      </div>
      <div className={styles.encryptionNote}>{getStep1EncryptionReminder()}</div>
    </>
  );

  const renderExternal = () => (
    <>
      {renderMethodStepHeader(stepIndexFor('where-kept'))}
      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="s1-external-note">
          {t('step.1.writing.external_note_label')}
        </label>
        <input
          id="s1-external-note"
          className={styles.input}
          placeholder={t('step.1.writing.external_note_placeholder')}
          value={localMeta.externalNote || ''}
          onChange={(e) => {
            const nextMeta = { ...localMeta, externalNote: e.target.value };
            setLocalMeta(nextMeta);
            scheduleSave(currentPayload, nextMeta);
          }}
        />
      </div>
      {renderPresentationPlan(stepIndexFor('presentation'))}
      {renderReviewSection()}
    </>
  );

  const renderLongDraftSection = (
    options: { ariaLabel: string; placeholder?: string; title?: string },
    editorRef?: React.RefObject<HTMLDivElement | null>
  ) => (
    <div className={styles.longDraftSection}>
      {options.title && <h3 className={styles.longDraftSectionTitle}>{options.title}</h3>}
      {renderStoryTextarea({ ...options, editorRef })}
    </div>
  );

  const renderTyped = () => (
    <>
      {renderMethodStepHeader(stepIndexFor('your-story'))}
      <div style={{ marginBottom: 8 }}>
        <button
          type="button"
          className={styles.btn}
          onClick={() => {
            const insert = async () => {
              const nextPayload = {
                ...currentPayload,
                contentText: getStep1WritingTemplate(),
              };
              setPayload(nextPayload);
              await persistAll(nextPayload, localMeta, 'destructive', 'Inserted story template');
            };
            if (contentText.trim()) {
              showConfirm({
                title: t('step.1.writing.insert_template_title'),
                message: t('step.1.writing.insert_template_message'),
                confirmText: t('step.1.writing.insert_template'),
                cancelText: t('step.1.writing.cancel'),
                onConfirm: () => void insert(),
              });
              return;
            }
            void insert();
          }}
        >
          {t('step.1.writing.insert_template')}
        </button>
      </div>
      {!isLongDraft &&
        renderStoryTextarea({
          ariaLabel: t('step.1.writing.story_aria'),
          placeholder: t('step.1.writing.story_placeholder'),
        })}
      {isLongDraft &&
        contentText.trim() &&
        renderLongDraftSection(
          {
            ariaLabel: t('step.1.writing.story_aria'),
            placeholder: t('step.1.writing.story_placeholder'),
            title: t('step.1.writing.section_your_story'),
          },
          draftEditorRef
        )}
      {renderPathTail()}
    </>
  );

  const renderUpload = () => (
    <>
      {renderMethodStepHeader(stepIndexFor('upload'))}
      <div className={styles.uploadZone}>
        <p>{t('step.1.writing.upload_choose')}</p>
        {/* Native file chrome is browser-locale only — hide it and use catalog labels. */}
        <input
          ref={uploadFileInputRef}
          type="file"
          accept=".txt,.md,.docx,.pdf"
          className={styles.fileInputHidden}
          onChange={(e) => {
            const f = e.target.files?.[0];
            setUploadPickerFileName(f?.name || '');
            if (f) handleUpload(f);
            e.target.value = '';
          }}
        />
        <div className={styles.filePickerRow}>
          <button
            type="button"
            className={styles.btn}
            onClick={() => uploadFileInputRef.current?.click()}
          >
            {t('step.1.writing.choose_file')}
          </button>
          <span className={styles.filePickerName}>
            {uploadPickerFileName ||
              currentPayload.originalFileName ||
              t('step.1.writing.no_file_chosen')}
          </span>
        </div>
        {uploadError && <p style={{ color: '#b00020' }}>{uploadError}</p>}
        {currentPayload.originalFileName && (
          <p style={{ marginTop: 12 }}>
            {t('step.1.writing.upload_file_label')}{' '}
            <strong>{currentPayload.originalFileName}</strong>{' '}
            <button type="button" className={styles.btnLink} onClick={() => downloadOriginalFile(currentPayload)}>
              {t('step.1.writing.download_original')}
            </button>
          </p>
        )}
      </div>
      {contentText && !isLongDraft && (
        <>
          {renderMethodStepHeader(stepIndexFor('review-edit'))}
          {renderStoryTextarea({ ariaLabel: t('step.1.writing.extracted_aria') })}
        </>
      )}
      {contentText &&
        isLongDraft && (
          <>
            {renderMethodStepHeader(stepIndexFor('review-edit'))}
            {renderLongDraftSection(
              {
                ariaLabel: t('step.1.writing.extracted_aria'),
                title: t('step.1.writing.section_extracted'),
              },
              draftEditorRef
            )}
          </>
        )}
      {renderPathTail()}
    </>
  );

  const renderWizardChapterTabs = () => (
    <div className={styles.chapterTabsBelow}>
      <span className={styles.chapterTabsLabel}>{t('step.1.writing.jump_to_section')}</span>
      <div className={styles.chapterTabs}>
        {STEP1_CHAPTERS.map((ch) => (
          <button
            key={ch.id}
            type="button"
            className={`${styles.chapterTab} ${currentPrompt?.chapterId === ch.id ? styles.chapterTabActive : ''}`}
            onClick={() => {
              const first = promptsForChapter(ch.id)[0];
              if (first) setWizardPromptIndex(globalPromptIndex(first.id));
            }}
          >
            {chapterTitle(ch.id)}
            {ch.optional ? t('step.1.writing.chapter_optional_suffix') : ''}
          </button>
        ))}
      </div>
    </div>
  );

  const renderWizard = () => (
      <>
        {renderMethodStepHeader(stepIndexFor('walkthrough'))}

        {currentPrompt ? (
          <>
            <p className={styles.promptProgress}>
              {t('step.1.writing.prompt_label')}{' '}
              <span className={styles.promptProgressNumber}>{wizardPromptIndex + 1}</span>{' '}
              {t('step.1.writing.prompt_of')}{' '}
              <span className={styles.promptProgressNumber}>
                {STEP1_LIFE_STORY_PROMPTS.length}
              </span>
            </p>
            <p className={styles.promptText}>{promptText(currentPrompt)}</p>
            {promptNudge(currentPrompt) ? (
              <p className={styles.promptHint}>{promptNudge(currentPrompt)}</p>
            ) : null}
            <textarea
              className={styles.textarea}
              style={{ minHeight: 160 }}
              value={currentAnswer?.examples?.[0] || ''}
              onChange={(e) => setWizardExample(e.target.value)}
              placeholder={promptPlaceholder(currentPrompt)}
            />
            <div className={styles.wizardNav}>
              <button type="button" className={styles.btn} onClick={goWizardPrev} disabled={wizardPromptIndex === 0}>
                {t('step.1.writing.previous')}
              </button>
              <button type="button" className={styles.btnPrimary} onClick={goWizardNext} disabled={isLastWizardPrompt}>
                {t('step.1.writing.next')}
              </button>
              <button type="button" className={styles.btn} onClick={skipWizardPrompt} disabled={isLastWizardPrompt}>
                {t('step.1.writing.skip')}
              </button>
            </div>
          </>
        ) : null}

        {renderWizardChapterTabs()}

        {renderMethodStepHeader(stepIndexFor('assemble'))}

        {!hasAssembledDraft && (
          <div className={styles.wizardNav}>
            <button
              type="button"
              className={`${styles.btn} ${isAssembling ? styles.btnBusy : ''}`}
              onClick={handleAssembleDraft}
              disabled={isAssembling}
              aria-busy={isAssembling}
            >
              {isAssembling
                ? t('step.1.writing.assembling')
                : t('step.1.writing.assemble_cta')}
            </button>
          </div>
        )}

        {draftNeedsReassemble && (
          <div className={styles.assembleStatusBlock}>
            <p className={styles.assembleStaleNote}>{t('step.1.writing.assemble_stale_note')}</p>
            <button
              type="button"
              className={`${styles.btn} ${isAssembling ? styles.btnBusy : ''}`}
              onClick={handleAssembleDraft}
              disabled={isAssembling}
              aria-busy={isAssembling}
            >
              {isAssembling
                ? t('step.1.writing.updating_draft')
                : t('step.1.writing.update_draft_cta')}
            </button>
          </div>
        )}

        {draftIsCurrent && (
          <p className={styles.assembleCurrentNote}>
            {t('step.1.writing.draft_current_note', {
              words: formatWordCountLabel(stats.wordCount),
            })}
          </p>
        )}

        {hasAssembledDraft &&
          (isLongDraft
            ? renderLongDraftSection(
                {
                  ariaLabel: t('step.1.writing.assembled_aria'),
                  title: draftIsCurrent
                    ? t('step.1.writing.section_assembled_current')
                    : t('step.1.writing.section_assembled'),
                },
                draftEditorRef
              )
            : renderStoryTextarea({
                ariaLabel: t('step.1.writing.assembled_aria'),
                editorRef: draftEditorRef,
              }))}

        {renderPathTail()}
      </>
  );

  const renderDraftChecklist = () => {
    if (draftChecklistIssues.length === 0) return null;
    return (
      <div className={styles.coachSection}>
        {renderMethodStepHeader(stepIndexFor('draft-checklist'))}
        <p className={styles.coachHint}>{t('step.1.writing.checklist_hint')}</p>
        <div
          className={styles.coachFeedback}
          role="region"
          aria-label={t('step.1.writing.checklist_aria')}
        >
          <ul className={styles.coachList}>
            {draftChecklistIssues.map((issue) => {
              const isStrength = issue.severity === 'info';
              const hasTry = !isStrength && issue.recommendation.trim().length > 0;
              return (
                <li
                  key={issue.id}
                  className={`${styles.coachListItem} ${isStrength ? styles.coachListItemStrength : ''}`}
                >
                  {isStrength ? (
                    <p className={styles.coachStrengthLine}>{issue.feedback}</p>
                  ) : (
                    <>
                      <p className={styles.coachFeedbackLine}>{issue.feedback}</p>
                      {hasTry && (
                        <p className={styles.coachRecommendationLine}>
                          <span className={styles.coachFeedbackLabel}>
                            {t('step.1.writing.checklist_consider')}
                          </span>{' '}
                          {issue.recommendation}
                        </p>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  };

  const renderReviewSection = () => {
    if (!localMeta.mode) return null;
    return (
      <div className={styles.reviewSection} ref={reviewSectionRef}>
        {renderMethodStepHeader(stepIndexFor('review'))}
        {contentText.trim() && (
          <div className={styles.reviewActions}>
            <button type="button" className={styles.btn} onClick={() => setIsStoryViewOpen(true)}>
              {t('step.1.writing.view')}
            </button>
            <button type="button" className={styles.btn} onClick={() => setIsDownloadPickerOpen(true)}>
              {t('step.1.writing.download')}
            </button>
            <button type="button" className={styles.btn} onClick={handlePrint}>
              {t('step.1.writing.print')}
            </button>
            <button type="button" className={styles.btnPrimary} onClick={openStoryShare}>
              {t('step.1.writing.share_story')}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPathTail = () => (
    <>
      {showsDraftChecklist && renderDraftChecklist()}
      {renderPresentationPlan(stepIndexFor('presentation'))}
      {renderReviewSection()}
    </>
  );

  const renderMainView = () => {
    if (showPathPicker || !isStep1PathSelected(localMeta)) return renderPathPicker();
    switch (localMeta.mode) {
      case 'external':
        return renderExternal();
      case 'upload':
        return renderUpload();
      case 'typed':
        return renderTyped();
      case 'wizard':
        return renderWizard();
      default:
        return renderPathPicker();
    }
  };

  const renderStoryViewOverlay = () => {
    if (!isStoryViewOpen) return null;
    const html = generateFirstStepShareHtml(contentText, {
      lastEditedAt: firstStepLastEditedAt,
      includeShareNotice: false,
    });
    return (
      <div
        className={styles.storyViewOverlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="s1-story-view-title"
      >
        <div className={styles.storyViewToolbar}>
          <h2 id="s1-story-view-title" className={styles.storyViewToolbarTitle}>
            {t('step.1.writing.my_first_step')}
          </h2>
          <div className={styles.storyViewToolbarActions}>
            <button type="button" className={styles.btn} onClick={() => setIsDownloadPickerOpen(true)}>
              {t('step.1.writing.download')}
            </button>
            <button type="button" className={styles.btn} onClick={handlePrint}>
              {t('step.1.writing.print')}
            </button>
            <button type="button" className={styles.btnPrimary} onClick={() => setIsStoryViewOpen(false)}>
              {t('step.1.writing.close')}
            </button>
          </div>
        </div>
        <iframe
          className={styles.storyViewFrame}
          srcDoc={html}
          title={t('step.1.writing.my_first_step')}
        />
      </div>
    );
  };

  const renderDownloadPicker = () => {
    if (!isDownloadPickerOpen) return null;
    return (
      <div className={styles.subModalOverlay} onClick={() => setIsDownloadPickerOpen(false)}>
        <div
          className={styles.subModal}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="s1-download-title"
          aria-modal="true"
        >
          <div className={styles.subModalHeader}>
            <h2 id="s1-download-title">{t('step.1.writing.download_title')}</h2>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => setIsDownloadPickerOpen(false)}
              aria-label={t('step.1.writing.close')}
            >
              ×
            </button>
          </div>
          <div className={styles.subModalBody}>
            <p className={styles.subModalIntro}>{t('step.1.writing.download_intro')}</p>
            <div className={styles.downloadFormatActions}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => void handleDownload('pdf')}
              >
                {t('step.1.writing.download_pdf')}
              </button>
              <button type="button" className={styles.btn} onClick={() => void handleDownload('docx')}>
                {t('step.1.writing.download_docx')}
              </button>
              <button type="button" className={styles.btn} onClick={() => void handleDownload('txt')}>
                {t('step.1.writing.download_txt')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderClearAllDialog = () => {
    if (!isClearAllOpen) return null;
    return (
      <div className={styles.subModalOverlay} onClick={() => !isClearingAll && setIsClearAllOpen(false)}>
        <div
          className={styles.subModal}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="s1-clear-title"
          aria-modal="true"
        >
          <div className={styles.subModalHeader}>
            <h2 id="s1-clear-title">{t('step.1.writing.clear_title')}</h2>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => !isClearingAll && setIsClearAllOpen(false)}
              aria-label={t('step.1.writing.close')}
              disabled={isClearingAll}
            >
              ×
            </button>
          </div>
          <div className={styles.subModalBody}>
            <p className={styles.subModalIntro}>{t('step.1.writing.clear_body_1')}</p>
            <p className={styles.subModalIntro}>{t('step.1.writing.clear_body_2')}</p>
            {contentText.trim() && (
              <div className={styles.clearAllExportActions}>
                <button type="button" className={styles.btn} onClick={() => setIsStoryViewOpen(true)}>
                  {t('step.1.writing.view')}
                </button>
                <button type="button" className={styles.btn} onClick={() => setIsDownloadPickerOpen(true)}>
                  {t('step.1.writing.download')}
                </button>
                <button type="button" className={styles.btn} onClick={handlePrint}>
                  {t('step.1.writing.print')}
                </button>
              </div>
            )}
            <label className={styles.clearAllCheckbox}>
              <input
                type="checkbox"
                checked={clearAllAcknowledged}
                onChange={(e) => setClearAllAcknowledged(e.target.checked)}
                disabled={isClearingAll}
              />
              <span>{t('step.1.writing.clear_ack')}</span>
            </label>
          </div>
          <div className={styles.subModalFooter}>
            <button
              type="button"
              className={styles.btn}
              onClick={() => setIsClearAllOpen(false)}
              disabled={isClearingAll}
            >
              {t('step.1.writing.cancel')}
            </button>
            <button
              type="button"
              className={styles.btnDanger}
              onClick={() => void handleClearAll()}
              disabled={!clearAllAcknowledged || isClearingAll}
            >
              {isClearingAll ? t('step.1.writing.clearing') : t('step.1.writing.clear_title')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRecoverPanel = () => {
    if (!isRecoverOpen) return null;
    return (
      <Step1RecoverOverlay
        userBackupKey={userBackupKey}
        archiveSummaries={archiveSummaries}
        isArchivesLoading={isArchivesLoading}
        onClose={() => setIsRecoverOpen(false)}
        onRestoreArchive={restoreArchive}
        onRestoreLocal={restoreLocalBackup}
      />
    );
  };

  const renderDraftEditorSubModal = () => {
    if (!isDraftEditorOpen || !draftEditorConfig) return null;
    const editStats = estimateReadTime(draftEditBuffer);
    const editLengthLabel = formatStoryLengthLabel(editStats);
    const isDirty = draftEditBuffer !== draftEditBaseline;

    return (
      <div className={styles.draftEditorOverlay} onClick={requestCloseDraftEditor}>
        <div
          className={styles.draftEditorModal}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="s1-draft-editor-title"
          aria-modal="true"
        >
          <div className={styles.draftEditorHeader}>
            <div className={styles.draftEditorHeaderInner}>
              <h2 id="s1-draft-editor-title">{draftEditorConfig.title}</h2>
              {editLengthLabel && <p className={styles.draftEditorHeaderMeta}>{editLengthLabel}</p>}
            </div>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={requestCloseDraftEditor}
              aria-label={t('step.1.writing.draft_editor_close_aria')}
            >
              ×
            </button>
          </div>
          <div className={styles.draftEditorBody}>
            <textarea
              className={styles.draftEditorTextarea}
              value={draftEditBuffer}
              onChange={(e) => setDraftEditBuffer(e.target.value)}
              placeholder={draftEditorConfig.placeholder}
              aria-label={draftEditorConfig.ariaLabel}
              autoFocus
            />
          </div>
          <div className={styles.draftEditorFooter}>
            <span className={styles.draftEditorDirtyNote}>
              {isDirty ? t('step.1.writing.unsaved_changes') : '\u00a0'}
            </span>
            <div className={styles.draftEditorFooterActions}>
              <button type="button" className={styles.btn} onClick={requestCloseDraftEditor}>
                {t('step.1.writing.cancel')}
              </button>
              <button type="button" className={styles.btnPrimary} onClick={saveDraftEditor}>
                {t('step.1.writing.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="s1-writing-title">
          <div className={styles.header}>
            <div className={styles.headerInner}>
              <h2 id="s1-writing-title">{t('ui.FIRST_STEP_WRITING_WORKSPACE')}</h2>
              {isStep1PathSelected(localMeta) && !showPathPicker && renderMethodBanner()}
            </div>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label={t('step.1.writing.close')}
            >
              ×
            </button>
          </div>

          <div ref={bodyRef} className={`${styles.body} ${isBodyBusy ? styles.bodyBusy : ''}`}>
            {showMissingStoryOffer && (
              <div className={styles.missingStoryOffer}>
                <p>{t('step.1.writing.missing_story')}</p>
                <div className={styles.missingStoryOfferActions}>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    onClick={() => void openRecoverPanel()}
                  >
                    {t('step.1.writing.view_earlier_saves')}
                  </button>
                  <button
                    type="button"
                    className={styles.btnLink}
                    onClick={() => setShowMissingStoryOffer(false)}
                  >
                    {t('step.1.writing.dismiss')}
                  </button>
                </div>
              </div>
            )}
            {renderMainView()}
          </div>

          <div className={styles.footer}>
            <div className={styles.footerLeft}>
              <span className={saveStatus === 'error' ? styles.saveStatusError : styles.saveStatus}>
                {saveStatus === 'saving' && t('step.1.writing.saving')}
                {saveStatus === 'saved' && t('step.1.writing.saved')}
                {saveStatus === 'error' && t('step.1.writing.save_error')}
              </span>
              {saveStatus === 'error' && (
                <button type="button" className={styles.btnLink} onClick={() => void openRecoverPanel()}>
                  {t('step.1.writing.something_missing')}
                </button>
              )}
            </div>
            {hasClearableContent && (
              <div className={styles.footerCenter}>
                <button type="button" className={styles.btnLinkMuted} onClick={openClearAllDialog}>
                  {t('step.1.writing.clear_footer_link')}
                </button>
              </div>
            )}
            <div className={styles.footerRight}>
              <button type="button" className={styles.btn} onClick={onClose}>
                {t('step.1.writing.close')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {renderRecoverPanel()}

      {renderDownloadPicker()}

      {renderClearAllDialog()}

      {renderStoryViewOverlay()}

      {renderDraftEditorSubModal()}

      <StepShareDialog
        isOpen={isStoryShareOpen}
        onClose={() => setIsStoryShareOpen(false)}
        user={user}
        stepTitle={tStep('step.page.title', 1)}
        shareName="My First Step"
        documentType="step-1-first-step"
        generateHtml={() =>
          generateFirstStepShareHtml(contentText, {
            lastEditedAt: firstStepLastEditedAt,
            includeShareNotice: true,
          })
        }
        canGenerate={() => contentText.trim().length > 0}
        emptyMessage={t('step.1.writing.share_empty')}
      />
    </>
  );
};

export default Step1WritingModal;
